import { RJSFSchema } from '@rjsf/utils';
import { DnsRecord, DnsRecordStatus, EmailSendingDomain, SendingDomainStatus } from '@/models/emailSendingDomains';

type PrepareStepParams = Record<string, { reactJsonSchemaForm?: DomainFlowStepFormConfig & { formId?: string } }> | null | undefined;

export type DomainFlowStepFormConfig = {
  schema: RJSFSchema;
  uiSchema: Record<string, unknown>;
  title?: string;
  description?: string;
};

export type DomainOption = {
  id: string;
  name: string;
};

const collectDefaults = (schema: RJSFSchema | undefined): Record<string, unknown> => {
  const data: Record<string, unknown> = {};
  const props = (schema as { properties?: Record<string, { default?: unknown }> })?.properties ?? {};

  for (const [key, prop] of Object.entries(props)) {
    if (prop?.default !== undefined) {
      data[key] = prop.default;
    }
  }

  return data;
};

const readString = (data: Record<string, unknown>, keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return undefined;
};

export const normalizeSendingDomainStatus = (value: unknown): SendingDomainStatus => {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase();

  if (normalized === 'YES' || normalized === 'TRUE') {
    return 'VERIFIED';
  }

  if (normalized === 'NO' || normalized === 'FALSE') {
    return 'PENDING';
  }

  if (normalized === 'VERIFIED' || normalized === 'ACTIVE' || normalized === 'VERIFIED_DNS') {
    return 'VERIFIED';
  }

  if (normalized.includes('VERIFIED') && !normalized.includes('UNVERIFIED')) {
    return 'VERIFIED';
  }

  if (normalized.includes('UNVERIFIED')) {
    return 'PENDING';
  }

  if (normalized === 'VERIFYING' || normalized === 'IN_PROGRESS') {
    return 'VERIFYING';
  }

  if (normalized === 'FAILED' || normalized === 'ERROR') {
    return 'FAILED';
  }

  return 'PENDING';
};

export const normalizeDnsRecordStatus = (value: unknown): DnsRecordStatus => {
  const normalized = String(value ?? '')
    .trim()
    .toUpperCase();

  if (normalized === 'OK' || normalized === 'VALID' || normalized === 'VERIFIED') {
    return 'OK';
  }

  if (normalized === 'FAILED' || normalized === 'ERROR') {
    return 'FAILED';
  }

  return 'PENDING';
};

const normalizeRequired = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = String(value ?? '')
    .trim()
    .toLowerCase();

  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'required';
};

const parseRecordsValue = (value: unknown): DnsRecord[] => {
  if (!value) {
    return [];
  }

  let parsed: unknown = value;

  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed
    .map((record) => {
      if (!record || typeof record !== 'object') {
        return null;
      }

      const item = record as Record<string, unknown>;
      const host = readString(item, ['host', 'name', 'hostname', 'record_host']);
      const recordValue = readString(item, ['value', 'content', 'record_value', 'target']);

      if (!host || !recordValue) {
        return null;
      }

      return {
        host,
        value: recordValue,
        status: normalizeDnsRecordStatus(item['status'] ?? item['record_status'] ?? item['validation_status']),
        type: readString(item, ['type', 'record_type']),
        label: readString(item, ['label', 'purpose', 'description']),
        required: normalizeRequired(item['required'] ?? item['is_required'] ?? item['record_required']),
      } as DnsRecord;
    })
    .filter((record): record is DnsRecord => record !== null);
};

export const normalizeDnsRecords = (data: Record<string, unknown>): DnsRecord[] => {
  const directRecords = parseRecordsValue(data['dns_records'] ?? data['records'] ?? data['dnsRecords'] ?? data['domain_records']);

  if (directRecords.length > 0) {
    return directRecords;
  }

  const indexedRecords: DnsRecord[] = [];

  for (let index = 0; index < 20; index += 1) {
    const host = readString(data, [`record_${index}_host`, `dns_record_${index}_host`]);
    const value = readString(data, [`record_${index}_value`, `dns_record_${index}_value`]);

    if (!host || !value) {
      continue;
    }

    indexedRecords.push({
      host,
      value,
      status: normalizeDnsRecordStatus(data[`record_${index}_status`] ?? data[`dns_record_${index}_status`]),
      type: readString(data, [`record_${index}_type`, `dns_record_${index}_type`]),
      label: readString(data, [`record_${index}_label`, `dns_record_${index}_label`]),
      required: normalizeRequired(data[`record_${index}_required`] ?? data[`dns_record_${index}_required`]),
    });
  }

  return indexedRecords;
};

const parseHostValueText = (text: string): { host?: string; value?: string } => {
  const hostMatch = text.match(/Host:\s*(.+?)(?:\r?\n|$)/i);
  const valueMatch = text.match(/Value:\s*(.+?)(?:\r?\n|$)/is);

  return {
    host: hostMatch?.[1]?.trim(),
    value: valueMatch?.[1]?.trim(),
  };
};

const parseDnsRecordFieldTitle = (title: string): { type?: string; status: DnsRecordStatus } => {
  const lower = title.toLowerCase();
  let status: DnsRecordStatus = 'PENDING';

  if (lower.includes('(pass)') || lower.includes('(ok)')) {
    status = 'OK';
  } else if (lower.includes('(fail)') || lower.includes('(failed)') || lower.includes('(error)')) {
    status = 'FAILED';
  }

  const typeMatch = title.match(/—\s*([A-Z0-9]+)\s*\(/i);

  return {
    type: typeMatch?.[1],
    status,
  };
};

export type DnsRecordPurposeKey = 'emailRouting' | 'dkimSigning' | 'dmarc' | 'linkTracking';

export const inferDnsRecordPurpose = (host: string, value: string): DnsRecordPurposeKey | undefined => {
  const hostLower = host.toLowerCase();
  const valueLower = value.toLowerCase();

  if (hostLower.includes('_domainkey')) {
    return 'dkimSigning';
  }

  if (hostLower.includes('_dmarc')) {
    return 'dmarc';
  }

  if (hostLower.includes('mt-link') || valueLower.includes('t.mailtrap')) {
    return 'linkTracking';
  }

  if (valueLower.includes('smtp.mailtrap.live')) {
    return 'emailRouting';
  }

  return undefined;
};

export const extractDnsRecordsFromStep2 = (prepareParams: PrepareStepParams): DnsRecord[] => {
  const schema = prepareParams?.['step2']?.reactJsonSchemaForm?.schema;
  const flowData = extractFlowParamsFromStep2(prepareParams);
  const properties = (schema as { properties?: Record<string, { default?: unknown; title?: string }> })?.properties ?? {};

  const dnsRecords = Object.entries(properties)
    .filter(([key]) => /^dns_record_\d+$/.test(key))
    .sort(([keyA], [keyB]) => {
      const numA = Number.parseInt(keyA.replace('dns_record_', ''), 10);
      const numB = Number.parseInt(keyB.replace('dns_record_', ''), 10);
      return numA - numB;
    })
    .map(([key, prop]) => {
      const rawValue = flowData[key] ?? prop?.default;
      const text = typeof rawValue === 'string' ? rawValue : '';
      const { host, value } = parseHostValueText(text);

      if (!host || !value) {
        return null;
      }

      const titleMeta = parseDnsRecordFieldTitle(String(prop?.title ?? ''));
      const purposeKey = inferDnsRecordPurpose(host, value);

      return {
        host,
        value,
        status: titleMeta.status,
        type: titleMeta.type,
        label: purposeKey,
        required: true,
      } as DnsRecord;
    })
    .filter((record): record is DnsRecord => record !== null);

  if (dnsRecords.length > 0) {
    return dnsRecords;
  }

  return normalizeDnsRecords(flowData);
};

const NO_DOMAINS_SENTINEL_PATTERNS = [/^--\s*no available domains\s*--$/i, /^no available domains$/i];

export const isNoAvailableDomainsSentinel = (value: string): boolean => {
  const normalized = value.trim();

  if (!normalized) {
    return true;
  }

  return NO_DOMAINS_SENTINEL_PATTERNS.some((pattern) => pattern.test(normalized));
};

export const isValidDomainId = (domainId: string): boolean => !isNoAvailableDomainsSentinel(domainId);

export const isNoAvailableDomainsStep1 = (prepareParams: PrepareStepParams): boolean => {
  const step1Form = prepareParams?.['step1']?.reactJsonSchemaForm;
  const domainIdProperty = (
    step1Form?.schema?.properties as Record<string, { enum?: unknown[]; oneOf?: Array<{ const?: unknown; title?: string }> }> | undefined
  )?.['domain_id'];

  if (!domainIdProperty) {
    return true;
  }

  let candidateIds: string[] = [];

  if (Array.isArray(domainIdProperty.oneOf) && domainIdProperty.oneOf.length > 0) {
    candidateIds = domainIdProperty.oneOf.map((option) => String(option.const ?? '')).filter(Boolean);
  } else {
    candidateIds = (domainIdProperty.enum ?? []).map(String).filter(Boolean);
  }

  if (candidateIds.length === 0) {
    return true;
  }

  const validIds = candidateIds.filter((id) => !isNoAvailableDomainsSentinel(id));

  if (validIds.length === 0) {
    return true;
  }

  return false;
};

export const extractDomainOptionsFromStep1 = (prepareParams: PrepareStepParams): DomainOption[] => {
  if (isNoAvailableDomainsStep1(prepareParams)) {
    return [];
  }

  const schema = prepareParams?.['step1']?.reactJsonSchemaForm?.schema;
  const domainIdProperty = (
    schema?.properties as Record<string, { enum?: unknown[]; enumNames?: unknown[]; oneOf?: Array<{ const?: unknown; title?: string }> }> | undefined
  )?.['domain_id'];

  if (!domainIdProperty) {
    return [];
  }

  if (Array.isArray(domainIdProperty.oneOf) && domainIdProperty.oneOf.length > 0) {
    return domainIdProperty.oneOf
      .map((option) => ({
        id: String(option.const ?? ''),
        name: String(option.title ?? option.const ?? ''),
      }))
      .filter((option) => option.id && isValidDomainId(option.id));
  }

  const enumValues = (domainIdProperty.enum ?? []).map(String).filter(Boolean);
  const enumNames = (domainIdProperty.enumNames ?? enumValues).map(String);

  return enumValues
    .map((id, index) => ({
      id,
      name: enumNames[index] ?? id,
    }))
    .filter((option) => isValidDomainId(option.id));
};

export const extractFlowParamsFromStep2 = (prepareParams: PrepareStepParams): Record<string, unknown> => {
  const schema = prepareParams?.['step2']?.reactJsonSchemaForm?.schema;
  return collectDefaults(schema);
};

export const extractStepFormConfig = (prepareParams: PrepareStepParams, stepKey: 'step1' | 'step2'): DomainFlowStepFormConfig | null => {
  const stepConfig = prepareParams?.[stepKey]?.reactJsonSchemaForm;

  if (!stepConfig?.schema) {
    return null;
  }

  return {
    schema: stepConfig.schema,
    uiSchema: stepConfig.uiSchema ?? {},
    title: stepConfig.title,
    description: stepConfig.description,
  };
};

export const extractStep2FormConfig = (prepareParams: PrepareStepParams): DomainFlowStepFormConfig | null => extractStepFormConfig(prepareParams, 'step2');

export const extractDomainDetailFromStep2 = (prepareParams: PrepareStepParams, domainId: string, domainName?: string): EmailSendingDomain => {
  const flowData = extractFlowParamsFromStep2(prepareParams);

  return {
    id: domainId,
    name: domainName ?? readString(flowData, ['domain_name', 'sending_domain', 'name', 'domain']) ?? domainId,
    status: normalizeSendingDomainStatus(flowData['dns_verified']),
    records: extractDnsRecordsFromStep2(prepareParams),
    createdAt: readString(flowData, ['created_at', 'createdAt', 'created_on', 'added_at']) ?? new Date(0).toISOString(),
    lastCheckedAt: readString(flowData, ['last_checked_at', 'lastCheckedAt', 'checked_at', 'updated_at']),
  };
};

export const buildInfoFlowParams = (domainId: string, step2Params: Record<string, unknown>): Record<string, unknown> => ({
  ...step2Params,
  domain_id: domainId,
});
